import axios, { waitForTokenRefresh } from '../utils/axios'
import { apis } from "../constants/apis";
import { getStaticConfigs } from "../helpers/common";
import httpService from "./httpService";

/**
 * `GET /api/v1/menus/visible` returns one flat list for both the side menu and
 * the bottom menu. Each row carries its icon as a base64 data URI and the rest
 * of its config as an `extra` JSON *string* — the same object shape the old
 * side-bar.json used, so mapping keeps MenuBar untouched:
 *   { active, icon, id, label: { lang_key, lang_vi, lang_en }, role, route,
 *     onClick, total, position, query_filter }
 */
export async function getVisibleMenus()
{
      return httpService.get(apis.menu.visible);
}

const parseExtra = (raw: any): any =>
{
      if (raw && typeof raw === "object") return raw;
      if (typeof raw !== "string" || !raw.trim()) return {};

      try
      {
            return JSON.parse(raw);
      } catch
      {
            return {};
      }
};

const toFilterValue = (entry: any) =>
      entry && typeof entry === "object" ? entry.id ?? entry.code ?? entry.value : entry;

/**
 * A row can pin the list page it opens to a fixed server-side filter. `query_filter`
 * holds the *name* of the query param, and the row itself ships the value under that
 * same name — e.g. `query_filter: "order_categories"` pairs with
 * `order_categories: [{ id: 2, code: "weekly_photo", ... }, ...]`, which the list
 * request sends as `order_categories=2&order_categories=3&...`.
 *
 * The descriptor keeps the entries too, so a page can offer them as filter options
 * instead of only applying them wholesale.
 */
const toQueryFilter = (row: any, config: any) =>
{
      const param = config?.query_filter;

      if (typeof param !== "string" || !param.trim()) return undefined;

      const raw = row?.[param] ?? config?.[param];

      if (raw === undefined || raw === null) return undefined;

      const entries = (Array.isArray(raw) ? raw : [raw]).filter(
            (entry: any) => entry?.is_active !== false,
      );

      const values = entries
            .map(toFilterValue)
            .filter((value: any) => value !== undefined && value !== null);

      if (!values.length) return undefined;

      return { param, entries, values, params: { [param]: values } };
};

const toMenuItem = (row: any) =>
{
      // The config lives in `extra`; `extra_data` is the newer name some rows ship it
      // under, so both are read and the newer one wins on conflict.
      const config = { ...parseExtra(row?.extra), ...parseExtra(row?.extra_data) };

      return {
            ...config,
            id: config?.id || row?.key,
            // Only is_active rows reach here, and MenuBar filters on `active`.
            active: true,
            // The API ships the icon as a base64 data URI; extra.icon still holds
            // the legacy static path, kept as the fallback.
            icon: row?.icon ? { type: "embed_base64", name: row.icon } : config?.icon,
            label: config?.label ?? { lang_key: row?.name },
            route: config?.route ?? row?.path ?? undefined,
            // `route` and `path` can disagree (the row's own path vs the one the config
            // points at); both are kept so a page can recognise itself by either.
            path: row?.path ?? config?.route ?? undefined,
            queryFilter: toQueryFilter(row, config),
      };
};

/** Wraps items in the `{ menu: [{ items }] }` shape MenuBar reads from Redux. */
const wrapItems = (items: any[]) => ({
      menu: [{ active: true, id: "vmrs_cbd_user", items }],
});

/**
 * Splits the endpoint's rows into the side menu and the bottom menu — an item
 * belongs to the bottom one when its `extra.position` is "bottom".
 */
export const mapVisibleMenus = (rows: any) =>
{
      if (!Array.isArray(rows)) return undefined;

      const items = rows
            .filter((row) => row?.is_active)
            .slice()
            .sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
            .map(toMenuItem);

      return {
            side: wrapItems(items.filter((item) => item?.position !== "bottom")),
            bottom: wrapItems(items.filter((item) => item?.position === "bottom")),
      };
};

// getSideMenu() and getSideBottomMenu() are dispatched together on startup and
// both read the same endpoint, so the in-flight request is shared.
let visibleMenusRequest: Promise<any> | null = null;

const readMenus = () =>
      getVisibleMenus().then((res: any) =>
            mapVisibleMenus(res?.data?.data?.menu ?? res?.data?.menu),
      );

/**
 * The menu the whole sidebar is built from. A failure here isn't harmless: the
 * caller falls back to the bundled demo menu, which the user then stares at —
 * with entries that may not be theirs — until they reload the page by hand.
 * That is worth one retry: the failures seen in practice (a token written a
 * moment earlier, a cold connection) don't repeat on the second attempt.
 */
async function loadVisibleMenus()
{
      if (!visibleMenusRequest)
      {
            visibleMenusRequest = readMenus()
                  .catch(async (error) =>
                  {
                        console.warn("Menu request failed, retrying once", error);

                        // The observed failure is a 400 from a stale token, while a
                        // request running alongside it gets a 401 and starts a
                        // refresh. Retrying before that refresh lands would fail the
                        // same way, so wait it out first.
                        await waitForTokenRefresh();

                        return readMenus();
                  })
                  .catch((error) =>
                  {
                        // Still nothing. Say so rather than letting the caller's
                        // fallback quietly stand in for the real menu.
                        console.error("Menu request failed, falling back", error);
                        return undefined;
                  })
                  .finally(() =>
                  {
                        visibleMenusRequest = null;
                  });
      }

      return visibleMenusRequest;
}

export async function getSideMenu()
{
      const staticConfigs = await getStaticConfigs();

      if (staticConfigs?.REACT_APP_AUTHENTICATION_METHOD === "OAUTH2")
      {
            const menus = await loadVisibleMenus();
            return { data: menus?.side };
      }

      return axios.get(
            `${staticConfigs?.REACT_APP_API_BASE_URL}/ailbl/configs/vmrs/side-bar`
      );
}

export async function getSideBottomMenu()
{
      const staticConfigs = await getStaticConfigs();

      if (staticConfigs?.REACT_APP_AUTHENTICATION_METHOD === "OAUTH2")
      {
            const menus = await loadVisibleMenus();
            return { data: menus?.bottom };
      }

      return axios.get(
            `${staticConfigs?.REACT_APP_API_BASE_URL}/ailbl/configs/vmrs/side-bottom`
      );
}
